import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    CategoryFacetModel,
    FacetModel,
    PriceFacetModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import { formatPriceRangeFacet } from "@insite/content-library/Widgets/ProductList/ProductListPriceFilters";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Search from "@insite/mobius/Icons/Search";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency },
        },
    } = state;
    const pagination = getProductListDataViewProperty(state, "pagination");
    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;

        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;
        const mergeAttributeTypeFacets = (
            unfilteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
            filteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
        ) => {
            if (!unfilteredAttributeTypeFacets) {
                return filteredAttributeTypeFacets;
            }

            if (!filteredAttributeTypeFacets) {
                return unfilteredAttributeTypeFacets;
            }

            return unfilteredAttributeTypeFacets.concat(
                filteredAttributeTypeFacets.filter(
                    o => !unfilteredAttributeTypeFacets.some(p => p.attributeTypeId === o.attributeTypeId),
                ),
            );
        };

        return {
            loaded: true,
            stockedItemsOnly: productFilters.stockedItemsOnly ?? false,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts ?? false,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets || filteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: mergeAttributeTypeFacets(
                unfilteredProductCollection?.attributeTypeFacets,
                filteredProductCollection?.attributeTypeFacets,
            ),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributeValueIds,
            selectedCategoryId: productFilters.categoryId,
            currency,
            productFilters,
            pagination,
            hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
        };
    }
    return {
        loaded: false,
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        brandFacets: [] as FacetModel[],
        productLineFacets: [] as FacetModel[],
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        attributeTypeFacets: [] as AttributeTypeFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        productFilters,
        pagination,
        hideSearchWithin: getSettingsCollection(state).searchSettings.searchProvider === "Commerce Search v3",
    };
};

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
    clearAllProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListSearchWithinStyles {
    wrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    filterTag?: TagPresentationProps;
    filterText?: TypographyPresentationProps;
    clearAllLink?: LinkPresentationProps;
    select?: SelectPresentationProps;
    selectWrapper?: InjectableCss;
    skipFiltersButtonWrapper?: InjectableCss;
    skipFiltersButton?: SkipNavStyles;
}

export const searchWithinStyles: ProductListSearchWithinStyles = {
    wrapper: {
        css: css`
            border: 0.5px solid ${COLORS.slightlyLightGray};
            opacity: 1;
            background-color: ${COLORS.background.base};
            padding: 7px;
        `,
    },
    titleText: {
        variant: "h6",
    },
    filterText: {
        variant: "h3",
        css: css`
            opacity: 1;
            color: ${getColor("primary")};
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.larger};
            font-weight: ${TYPOGRAPHY.weight.baseBold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallish};
            text-align: left;
            margin-bottom: 15px;
        `,
    },
    searchTextField: {
        iconProps: {
            src: Search,
            css: css`
                top: 0;
                right: 0;
                width: 30px;
                height: 30px;
                padding: 0 8px;
                > svg {
                    height: 16px;
                    width: 16px;
                    > g {
                        > path {
                            fill: ${getColor("secondary")};
                        }
                    }
                }
            `,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
                > div[class^="FormInputWrapper"] {
                    button[class^="StyledButton"] {
                        height: 30px;
                        padding: 0;
                        > span {
                            top: 0;
                            right: 0;
                            width: 30px;
                            height: 30px;
                            padding: 0;
                            background-color: ${COLORS.background.lightGrey};
                            border-radius: inherit;
                            border: 0.5px solid ${COLORS.graySwan};
                            border-left: none;
                        }
                    }
                }
            `,
            inputSelect: css`
                border: 0.5px solid ${COLORS.graySwan};
                opacity: 1;
                background-color: ${COLORS.background.lightGrey};
                padding: 0 9px 0 9px;
                height: 30px;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.smaller};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                ::placeholder {
                    opacity: 1;
                    color: ${getColor("secondary")};
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-size: ${TYPOGRAPHY.size.smaller};
                    font-weight: ${TYPOGRAPHY.weight.baseBold};
                    font-style: normal;
                    letter-spacing: ${TYPOGRAPHY.letterSpacing.vSmallish};
                    text-align: left;
                }
                &:focus {
                    border: 0.5px solid ${COLORS.graySwan};
                }
            `,
        },
    },
    filterTag: {
        css: css`
            opacity: 1;
            background-color: ${COLORS.background.grayWhisper};
            padding: 5px;
            margin: 5px 5px 0 0;
            min-height: 15px;
            border-radius: unset;
            display: inline-flex;
            max-width: 100%;
            > button[class^="ButtonWrapper"] {
                opacity: 1;
                background: ${getColor("secondary")};
                border-color: ${getColor("secondary")};
                height: 14px;
                flex-basis: 14px;
                border-radius: 50%;
                margin-left: 5px;
                margin-top: 2px;
                min-height: 14px;
                > span[class^="IconWrapper"] {
                    height: 14px;
                    width: 14px;
                }
            }
        `,
        typographyProps: {
            css: css`
                opacity: 1;
                color: ${getColor("secondary")};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.tiny};
                font-weight: ${TYPOGRAPHY.weight.base};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                text-align: left;
                margin: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                font-size: ${TYPOGRAPHY.size.tiny};
                            `,
                            null,
                        ],
                        "minMax",
                    )}
            `,
        },
    },
    clearAllLink: {
        css: css`
            display: flex;
            > span[class^="TypographyStyle"] {
                opacity: 1;
                color: ${getColor("secondary")};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.tiny};
                font-weight: ${TYPOGRAPHY.weight.base};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                text-align: left;
                margin: 0;
            }
        `,
    },
    selectWrapper: {
        css: css`
            div[class^="FormFieldStyle"] {
                label {
                    text-align: left !important;
                    width: auto !important;
                    margin-bottom: 20px !important;
                    color: rgba(17, 41, 69, 1);
                    font-family: Poppins;
                    font-size: 14px;
                    font-weight: 500;
                }
            }
        `,
    },
    select: {
        cssOverrides: {
            formInputWrapper: css`
                color: ${COLORS.secondaryNavyThree};
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-size: ${TYPOGRAPHY.size.smallest} !important;
                font-weight: ${TYPOGRAPHY.weight.baseBold};
            `,
        },
    },
};

const styles = searchWithinStyles;

const ProductListSearchWithin: FC<Props> = ({
    loaded,
    addProductFilters,
    productFilters,
    stockedItemsOnly,
    previouslyPurchasedProducts,
    brandFacets,
    productLineFacets,
    priceFacets,
    categoryFacets,
    attributeTypeFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    removeProductFilters,
    clearAllProductFilters,
    currency,
    pagination,
    hideSearchWithin
}) => {
    if (!loaded) {
        return null;
    }

    const [searchText, setSearchText] = useState("");
    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addProductFilters({ sort: event.currentTarget.value });
    };
    const clickRemoveStockedItemsOnly = () => {
        removeProductFilters({ stockedItemsOnly: false });
    };

    const clickRemovePreviouslyPurchasedProducts = () => {
        removeProductFilters({ previouslyPurchasedProducts: false });
    };

    const clickRemoveSearchHandler = (search: string) => {
        removeProductFilters({ searchWithinQueries: [search] });
    };

    const clickRemoveBrandHandler = (id: string) => {
        removeProductFilters({ brandIds: [id] });
    };

    const clickRemoveProductLineHandler = (id: string) => {
        removeProductFilters({ productLineIds: [id] });
    };

    const clickRemovePriceHandler = (price: string) => {
        removeProductFilters({ priceFilters: [price] });
    };

    const clickRemoveAttributeValueHandler = (id: string) => {
        removeProductFilters({ attributeValueIds: [id] });
    };

    const clickRemoveCategoryHandler = () => {
        removeProductFilters({ categoryId: selectedCategoryId });
    };

    const doSearch = () => {
        if (searchText.trim() !== "") {
            addProductFilters({ searchWithinQueries: [searchText] });
        }
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    let pageType: string;
    if (productFilters.pageProductLineId) {
        pageType = "Product Line";
    } else if (productFilters.pageBrandId) {
        pageType = "Brand";
    } else if (productFilters.pageCategoryId) {
        pageType = "Category";
    } else {
        pageType = "Results";
    }

    const label = translate(`Search Within ${pageType}`);

    const anyActiveFilters =
        searchWithinQueries?.length ||
        stockedItemsOnly ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        selectedCategoryId;

    if (hideSearchWithin) {
        return null;
    }
    return (
        <>
            <Typography {...styles.filterText} data-test-selector="productListFilterList">
                {translate("Filter by")}
            </Typography>
            <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                <ProductListPageDataContext.Consumer>
                    {({ ref }) => {
                        return ref ? (
                            <SkipNav
                                text={translate("Skip to Results")}
                                extendedStyles={styles.skipFiltersButton}
                                destination={ref}
                            />
                        ) : undefined;
                    }}
                </ProductListPageDataContext.Consumer>
            </StyledWrapper>
            <Hidden above="md">
                <StyledWrapper {...styles.selectWrapper}>
                    <Select
                        {...styles.select}
                        label={translate("Sort by :")}
                        value={pagination && pagination.sortType}
                        labelPosition="left"
                        onChange={onChangeSortHandler}
                        data-test-selector="productListSortSelect"
                    >
                        {pagination &&
                            pagination.sortOptions.map(sortOption => (
                                <option key={sortOption.sortType} value={sortOption.sortType}>
                                    {sortOption.displayName}
                                </option>
                            ))}
                    </Select>
                </StyledWrapper>
            </Hidden>
            <StyledWrapper {...styles.wrapper}>
                {/* <Typography forwardAs="label" htmlFor="productsKeywordSearch" {...styles.titleText}>
                    {label}
                </Typography> */}
                <TextField
                    {...styles.searchTextField}
                    iconClickableProps={{ onClick: doSearch, "aria-label": `Search within ${pageType}` }}
                    value={searchText}
                    onChange={event => setSearchText(event.currentTarget.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={translate("Search")}
                    type="text"
                    data-test-selector="productListSearchWithin"
                    id="productsKeywordSearch"
                />
                {previouslyPurchasedProducts && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={clickRemovePreviouslyPurchasedProducts}
                        data-test-selector="previouslyPurchasedProductsFilter"
                    >
                        {translate("Previously Purchased Only")}
                    </Tag>
                )}
                {stockedItemsOnly && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={clickRemoveStockedItemsOnly}
                        data-test-selector="stockedItemsOnlyFilter"
                    >
                        {translate("Stocked Items Only")}
                    </Tag>
                )}
                {searchWithinQueries?.map(s => (
                    <Tag
                        key={s}
                        {...styles.filterTag}
                        onDelete={() => clickRemoveSearchHandler(s)}
                        data-test-selector="searchWithinFilter"
                    >
                        {translate("Search")}: {s}
                    </Tag>
                ))}
                {brandFacets &&
                    selectedBrandIds?.map(id => (
                        <Tag
                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveBrandHandler(id)}
                            data-test-selector={`brandFilter${id}`}
                        >
                            {translate("Brand")}: {brandFacets?.find(o => o.id === id)?.name}
                        </Tag>
                    ))}
                {productLineFacets &&
                    selectedProductLineIds?.map(id => (
                        <Tag
                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemoveProductLineHandler(id)}
                            data-test-selector={`productLineFilter${id}`}
                        >
                            {translate("Product Line")}: {productLineFacets?.find(o => o.id === id)?.name}
                        </Tag>
                    ))}
                {selectedCategoryId && (
                    <Tag
                        {...styles.filterTag}
                        onDelete={() => clickRemoveCategoryHandler()}
                        data-test-selector={`categoryFilter${selectedCategoryId}`}
                    >
                        {translate("Category")}:{" "}
                        {categoryFacets?.find(o => o.categoryId === selectedCategoryId)?.shortDescription}
                    </Tag>
                )}
                {priceFacets &&
                    selectedPriceFilters?.map(id => (
                        <Tag
                            key={id}
                            {...styles.filterTag}
                            onDelete={() => clickRemovePriceHandler(id)}
                            data-test-selector={`priceFilter${id}`}
                        >
                            {translate("Price")}:{" "}
                            {formatPriceRangeFacet(
                                priceFacets?.find(o => o.minimumPrice.toString() === id),
                                currency,
                            )}
                        </Tag>
                    ))}
                {attributeTypeFacets &&
                    selectedAttributeValuesIds?.map(id => {
                        const attributeTypeFacet = attributeTypeFacets?.find(at =>
                            at.attributeValueFacets?.find(av => av.attributeValueId === id),
                        );
                        const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                            av => av.attributeValueId === id,
                        );

                        return (
                            <Tag
                                key={id}
                                {...styles.filterTag}
                                onDelete={() => clickRemoveAttributeValueHandler(id)}
                                data-test-selector={`attributeValueFilter${id}`}
                            >
                                {attributeTypeFacet?.nameDisplay}: {attributeValueFacet?.valueDisplay}
                            </Tag>
                        );
                    })}
                {anyActiveFilters && (
                    <Link
                        {...styles.clearAllLink}
                        onClick={() => clearAllProductFilters()}
                        data-test-selector="productListClearAll"
                    >
                        {translate("Clear All")}
                    </Link>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSearchWithin),
    definition: {
        group: "Product List",
        displayName: "Search Within",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
